import { observable, action, runInAction } from 'mobx';
import axios from 'axios';
import auth0 from 'auth0-js';
import { Modal } from 'antd';

export class UserStore {
    @observable currentUser = {
        timezone: 'UTC',
        theme: 'dark-theme',
        sound: true,
        last_read_announcement: 0
    };

    @observable enrollments = [];

    constructor({ rootStore }) {
        this.rootStore = rootStore;

        this.auth = new auth0.WebAuth({
            domain: 'auth.apextrader.io',
            clientID: 'gnzNyG6LIIQeW3A8SKDfoUKIlVpkI30C',
            //redirectUri: `${this.rootStore.env.hostUrl}/auth`,
            responseType: 'token id_token',
            scope: 'openid profile email '
        });

        this.accessToken = localStorage.getItem('accessToken') || null;
        this.idToken = localStorage.getItem('idToken') || null;
        this.expiresAt = localStorage.getItem('expiresAt') || 0;
    }

    @action async getProfile() {
        const profile = await axios.get(`${this.rootStore.env.authApi}user/metadata`, {
            headers: { Authorization: `Bearer ${this.idToken}` }
        });

        await this.auth.client.userInfo(this.accessToken, (err, user) => {
            runInAction(() => {
                console.log(err);
                if (err) {
                    this.login();
                }
                this.currentUser = {
                    uid: user.sub.replace('auth0|', ''),
                    picture: user.picture,
                    ...this.currentUser,
                    ...profile.data
                };
            });
        });
    }

    @action async updateProfile(data) {
        this.currentUser = { ...this.currentUser, ...data };

        return await axios.post(`${this.rootStore.env.authApi}user/metadata`, data, {
            headers: { Authorization: `Bearer ${this.idToken}` }
        });
    }

    login() {
        this.auth.authorize({ redirectUri: `${this.rootStore.env.hostUrl}/auth` });
    }

    register() {
        this.auth.authorize({ redirectUri: `${this.rootStore.env.hostUrl}/registered` });
    }

    /**
     * End the session for when the JWT expires and force a re-auth
     */
    registerAutoLogout() {
        const expiry = parseInt(localStorage.getItem('expiresAt'), 10);

        console.log(`session expiry: ${new Date(expiry)}`);
        if (!expiry) {
            this.login();
            return;
        }
        const resetTime = expiry - Date.now();
        console.log('refresh session in:', resetTime / 60000);

        if (Date.now() > expiry) {
            console.log('extending session');

            this.auth.checkSession(
                { redirectUri: `${this.rootStore.env.hostUrl}/auth` },
                async (err, authResult) => {
                    if (err) {
                        console.error(err);
                        this.logout();
                        Modal.error({
                            title: 'Session expired, you will be logged out',
                            onOk: () => {
                                this.props.store.userStore.login();
                            }
                        });
                        return;
                    }

                    let expiresAt = authResult.expiresIn * 1000 + new Date().getTime();
                    localStorage.setItem('accessToken', authResult.accessToken);
                    localStorage.setItem('idToken', authResult.idToken);
                    localStorage.setItem('expiresAt', expiresAt);
                    await this.getFirebaseToken();
                    console.log(`Extending session to: ${new Date(expiresAt)}`);

                    setTimeout(() => {
                        this.registerAutoLogout();
                    }, expiresAt);
                }
            );
            return;
        }

        setTimeout(() => {
            this.registerAutoLogout();
        }, resetTime);
    }

    /**
     * Get registered enrollments for MFA
     * @returns {Promise<void>}
     */
    async getEnrollments() {
        const enrollments = await axios.get(`${this.rootStore.env.authApi}user/enrollments`, {
            headers: { Authorization: `Bearer ${this.idToken}` }
        });

        runInAction(() => (this.enrollments = enrollments.data));
    }

    /**
     * Reset MFA enrollments
     * @param enrollment
     * @returns {Promise<void>}
     */
    async deleteEnrollments(enrollment) {
        await axios.get(
            `${this.rootStore.env.authApi}user/delete_enrollments?id=${enrollment.id}`,
            {
                headers: { Authorization: `Bearer ${this.idToken}` }
            }
        );

        runInAction(() => (this.enrollments = []));
    }

    /**
     * @returns Promise authentication was successful
     */
    handleAuthentication(newRegistration) {
        return new Promise((resolve, reject) => {
            this.auth.parseHash(async (err, authResult) => {
                if (authResult && authResult.accessToken && authResult.idToken) {
                    if (newRegistration) {
                        const uid = authResult.idTokenPayload.sub.replace('auth0|', '');
                        console.log('Tracking new user', uid);
                        window.$FPROM.trackSignup({ uid: uid }, () => {
                            console.log('FPTX complete');
                        });
                    }
                    let expiresAt = authResult.expiresIn * 1000 + new Date().getTime();
                    this.accessToken = authResult.accessToken;
                    this.idToken = authResult.idToken;
                    this.expiresAt = expiresAt;

                    localStorage.setItem('accessToken', authResult.accessToken);
                    localStorage.setItem('idToken', authResult.idToken);
                    localStorage.setItem('expiresAt', expiresAt);

                    await this.getFirebaseToken();
                    resolve();
                } else if (err) {
                    reject(err);
                }
            });
        });
    }

    async getFirebaseToken() {
        try {
            const response = await axios.get(`${this.rootStore.env.authApi}fb_token`, {
                headers: {
                    Authorization: `Bearer ${this.idToken}`
                }
            });
            this.rootStore.fbAuth.signInWithCustomToken(response.data.firebaseToken);
        } catch (e) {
            alert('Error fetching token, please try again');
        }
    }

    logout() {
        //this.rootStore.fbAuth.signOut();
        this.auth.logout({
            returnTo: this.rootStore.env.hostUrl,
            client_id: 'gnzNyG6LIIQeW3A8SKDfoUKIlVpkI30C'
        });
        this.rootStore.fbAuth.signOut();
        this.accessToken = null;
        this.idToken = null;
        this.expiresAt = null;
        localStorage.setItem('accessToken', null);
        localStorage.setItem('idToken', null);
        localStorage.setItem('expiresAt', null);
    }
}



// WEBPACK FOOTER //
// ./src/stores/UserStore.js